"use client"

import { useState } from "react"
import { Flag, FlagOff, Loader2 } from "lucide-react"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import { ILeadershipRating } from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import MODAL_NAMES from "../../../dialogs/ModalName"

interface FlagRatingModalProps {
  selectedRow: ILeadershipRating | null
  onConfirm: (comment: string) => Promise<void>
}

export const FlagRatingModal = ({ selectedRow, onConfirm }: FlagRatingModalProps) => {
  const [userComment, setUserComment] = useState("")
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    await onConfirm(userComment)
    setLoading(false)
    setUserComment("")
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.FLAG_RATING_MODAL)
  }

  const handleCancel = () => {
    setUserComment("")
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.FLAG_RATING_MODAL)
  }

  if (!selectedRow) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-full ${selectedRow.isflagged ? "bg-green-100" : "bg-red-100"}`}>
            {selectedRow.isflagged ? (
              <FlagOff className="h-6 w-6 text-green-600" />
            ) : (
              <Flag className="h-6 w-6 text-red-600" />
            )}
          </div>
          <h3 className="text-xl font-semibold text-slate-900">
            {selectedRow.isflagged ? "Unflag Rating" : "Flag Rating"}
          </h3>
        </div>

        <p className="text-slate-600 mb-4">
          {selectedRow.isflagged
            ? "Are you sure you want to unflag this rating?"
            : "Please provide a reason for flagging this rating:"}
        </p>

        {!selectedRow.isflagged && (
          <div className="mb-6">
            <label htmlFor="userComment" className="block text-sm font-medium text-slate-700 mb-2">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              id="userComment"
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
              placeholder="Please explain why you are flagging this rating..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={3}
              required
            />
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (!selectedRow.isflagged && !userComment.trim())}
            className={`px-6 py-3 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2 ${
              selectedRow.isflagged ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {selectedRow.isflagged ? <FlagOff className="h-4 w-4" /> : <Flag className="h-4 w-4" />}
                Confirm
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
